import { useEffect, useRef } from 'react'
import { RangerChat } from './RangerChat'
import { useRanger } from '../hooks/useRanger'
import { RefreshCw, Sparkles, X } from '@/ui/icons'
import { RangerMark } from './RangerMark'

interface RangerChatModalProps {
  isOpen: boolean
  onClose: () => void
  tripId?: string
}

export function RangerChatModal({ isOpen, onClose, tripId }: RangerChatModalProps) {
  const panelRef = useRef<HTMLDivElement | null>(null)
  const { messages, sendMessage, loading, error, clearError, clearMessages } = useRanger(tripId)

  useEffect(() => {
    if (!isOpen) return

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose()
    }

    window.addEventListener('keydown', handleKeyDown)

    if (panelRef.current) panelRef.current.focus()

    return () => {
      window.removeEventListener('keydown', handleKeyDown)
    }
  }, [isOpen, onClose])

  if (!isOpen) return null

  return (
    <div className="fixed inset-0 z-[60] flex justify-end pointer-events-none">
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-label="Ranger AI Assistant"
        tabIndex={-1}
        className="pointer-events-auto w-full h-full sm:h-full sm:max-w-[360px] md:max-w-[400px] lg:max-w-[440px] bg-background border-l flex flex-col shadow-xl transition-transform duration-200 ease-out data-[state=closed]:translate-x-full data-[state=open]:translate-x-0"
        data-state={isOpen ? 'open' : 'closed'}
      >
        <div className="px-6 pt-6 pb-4 border-b border-gray-200 flex items-center justify-between flex-shrink-0">
          <div className="flex min-w-0 items-center gap-2">
            <div className="bg-adventure-100 rounded-full p-2 shrink-0">
              <RangerMark thinking={loading} className="w-5 h-5 text-adventure-600" />
            </div>
            <div className="min-w-0">
              <h2 className="text-base font-semibold leading-tight">Ranger AI Assistant</h2>
              {tripId && (
                <span className="inline-flex items-center gap-1 text-[11px] font-medium text-adventure-700 dark:text-adventure-400">
                  <Sparkles className="h-3 w-3" />
                  Trip-aware
                </span>
              )}
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            {messages.length > 0 && (
              <button
                type="button"
                onClick={clearMessages}
                className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
                aria-label="Start a new chat"
                title="Start a new chat"
              >
                <RefreshCw className="w-4 h-4" />
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              className="inline-flex items-center justify-center rounded-md p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-adventure-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background"
              aria-label="Close Ranger AI Assistant"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
        <div className="flex-1 min-h-0 overflow-hidden">
          <RangerChat
            tripId={tripId}
            messages={messages}
            sendMessage={sendMessage}
            loading={loading}
            error={error}
            clearError={clearError}
          />
        </div>
      </div>
    </div>
  )
}

